import { createContext, useCallback, useEffect, useRef, useState } from 'react'

import { TribeClient } from '@tribeplatform/gql-client/client'

import type { ClientContextProps, ClientProviderType } from './@types/index.js'
import { getTokens } from './lib/getTokens.js'
import { defaultTracker, TribeTracker } from './TrackerProvider.js'

export const ClientContext = createContext<ClientContextProps>({
  client: null,
  ready: undefined,
  boot: null,
  config: null,
})

const SSR = typeof window === 'undefined'

export const ClientProvider: ClientProviderType = ({
  children,
  config,
  autoBoot = true,
  onTokenUpdate,
}) => {
  const { accessToken, baseUrl, gatewayCacheControl } = config
  const loadingRef = useRef<boolean>(false)

  let graphqlUrl = baseUrl
  if (!graphqlUrl && typeof process !== 'undefined') {
    graphqlUrl = process?.env?.TRIBE_GQL_ENDPOINT
  }

  const [client, setClient] = useState<TribeClient>(
    new TribeClient({
      accessToken,
      graphqlUrl,
      onError: config.onError,
      onEvent: config.onEvent,
      notifyOnTokenExpiration: !SSR,
      gatewayCacheControl,
    }),
  )
  const ready = !!(client as any)?.client?.accessToken
  const update = useCallback(
    async config => {
      const res = await getTokens(config)
      const { client: newClient } = res
      setClient(newClient)
      loadingRef.current = false
      return res
    },
    [setClient, loadingRef],
  )

  useEffect(() => {
    if (autoBoot && !loadingRef.current && !ready) {
      loadingRef.current = true
      update(config)
    }
  }, [loadingRef, update, client, ready, config, autoBoot])

  const boot = () => {
    update(config)
  }

  return (
    <ClientContext.Provider
      value={{ client, ready, boot, config, onTokenUpdate }}
    >
      <TribeTracker value={config.tracker || defaultTracker}>
        {children}
      </TribeTracker>
    </ClientContext.Provider>
  )
}
